"use client"

import Link from "next/link"
import { Edit, Trash2, Eye, EyeOff } from "lucide-react"
import { deletePost } from "@/lib/supabase/queries"
import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { DeleteConfirmDialog } from "./DeleteConfirmDialog"

interface BlogPostWithTags {
  id: string
  slug: string
  title: string
  excerpt: string
  content: string
  author: string | null
  published: boolean
  publishedAt: Date | null
  createdAt: Date
  updatedAt: Date | null
  tags: { id: string; name: string; slug: string; createdAt: Date }[]
}

interface PostListProps {
  posts: BlogPostWithTags[]
}

export function PostList({ posts }: PostListProps) {
  const queryClient = useQueryClient()
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null)

  const handleDeleteClick = (id: string, title: string) => {
    setDeleteTarget({ id, title })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return

    const { id } = deleteTarget
    setDeleteTarget(null)
    setDeletingId(id)

    try {
      const result = await deletePost(id)
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ["admin-posts"] })
      } else {
        alert(result.error || "Failed to delete post")
      }
    } catch {
      alert("Failed to delete post")
    } finally {
      setDeletingId(null)
    }
  }

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date)
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-border">
        <p className="text-text-muted mb-4">No posts yet</p>
        <Link
          href="/admin/post/new"
          className="text-primary hover:underline"
        >
          Create your first post
        </Link>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-border overflow-hidden">
      <table className="w-full">
        <thead className="bg-bg-alt border-b border-border">
          <tr>
            <th className="text-left px-4 py-3 text-sm font-medium text-text">
              Title
            </th>
            <th className="text-left px-4 py-3 text-sm font-medium text-text hidden md:table-cell">
              Status
            </th>
            <th className="text-left px-4 py-3 text-sm font-medium text-text hidden lg:table-cell">
              Tags
            </th>
            <th className="text-left px-4 py-3 text-sm font-medium text-text hidden sm:table-cell">
              Date
            </th>
            <th className="text-right px-4 py-3 text-sm font-medium text-text">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {posts.map((post) => (
            <tr key={post.id} className="border-b border-border last:border-0">
              <td className="px-4 py-3">
                <Link
                  href={`/admin/post/edit?id=${post.id}`}
                  className="font-medium text-text hover:text-primary transition-colors"
                >
                  {post.title}
                </Link>
                <p className="text-sm text-text-muted truncate max-w-xs">
                  {post.excerpt}
                </p>
              </td>
              <td className="px-4 py-3 hidden md:table-cell">
                {post.published ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                    <Eye className="w-3 h-3" />
                    Published
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                    <EyeOff className="w-3 h-3" />
                    Draft
                  </span>
                )}
              </td>
              <td className="px-4 py-3 hidden lg:table-cell">
                <div className="flex flex-wrap gap-1">
                  {post.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag.id}
                      className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full"
                    >
                      {tag.name}
                    </span>
                  ))}
                  {post.tags.length > 3 && (
                    <span className="px-2 py-0.5 text-xs bg-bg-alt text-text-muted rounded-full">
                      +{post.tags.length - 3}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-text-muted hidden sm:table-cell">
                {formatDate(post.createdAt)}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  <Link
                    href={`/admin/post/edit?id=${post.id}`}
                    className="p-2 text-text-muted hover:text-primary transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => handleDeleteClick(post.id, post.title)}
                    disabled={deletingId === post.id}
                    className="p-2 text-text-muted hover:text-red-500 transition-colors disabled:opacity-50"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <DeleteConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title={deleteTarget?.title || ""}
      />
    </div>
  )
}
